import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  inject,
} from "@angular/core";

import { Editor, Element, Range, Transforms, createEditor } from "slate";
import { AngularEditor, withAngular } from "slate-angular";
import { withHistory } from "slate-history";

import { TextMarkComponent } from "./text-mark/text-mark.component";

export enum MarkTypes {
  bold = "bold",
  italic = "italic",
  underline = "underlined",
  strike = "strike",
  code = "code-line",
}

export interface CustomText {
  placeholder?: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
  text: string;
  "code-line"?: boolean;
}

export interface PlaceHolderElement {
  type: "placeholder";
  name: string;
  children: CustomText[];
}

const insertPlaceholder = (editor: Editor, name: string) => {
  const placeholder: PlaceHolderElement = {
    type: "placeholder",
    name: name,
    children: [{ text: "" }],
  };
  Transforms.insertNodes(editor, placeholder);
  Transforms.move(editor, { distance: 5 });
};

const withMentions = (editor) => {
  const { isVoid, isInline } = editor;
  editor.isInline = (element) => {
    return element.type === "placeholder" ? true : isInline(element);
  };
  editor.isVoid = (element) => {
    return element.type === "placeholder" ? true : isVoid(element);
  };
  return editor;
};

const insertMention = (editor: Editor, name: string) => {
  const mention: PlaceHolderElement = {
    type: "placeholder",
    name: name,
    children: [{ text: "" }],
  };
  Transforms.delete(editor);
  Transforms.insertNodes(editor, mention);
  Transforms.move(editor);
};

@Component({
  selector: "[raw-sql]",
  standalone: false,
  templateUrl: "./feed-management-raw-sql.component.html",
  styleUrls: ["./feed-management-raw-sql.component.scss"],
  host: {
    "data-slate-node": "text",
  },
})
export class FeedManagementRawSqlComponent implements OnInit {
  private renderer2 = inject(Renderer2);
  private cdr = inject(ChangeDetectorRef);

  searchText = "";
  suggestions: string[] = [];
  target: Range;
  activeIndex = 0;
  trigger = "{";

  value;

  editor: AngularEditor = withMentions(
    withHistory(withAngular(createEditor())),
  );

  @Input({ required: true }) set query(to: string) {
    this.value = this.query2object(to);
  }
  @Input() placeholders: string[];

  @ViewChild("mention", { read: TemplateRef, static: true })
  mentionTemplate: TemplateRef<any>;

  @ViewChild("suggestionList", { static: true })
  suggestionList: ElementRef;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit() {}

  renderElement = (element: Element) => {
    if ((element as any).type === "placeholder") {
      return this.mentionTemplate;
    }
    return null;
  };

  renderText = (text: any) => {
    return TextMarkComponent;
  };

  enter(event: Event) {
    event.stopPropagation();
    event.preventDefault();

    insertPlaceholder(this.editor, "test");
  }

  onKeydown = (event: KeyboardEvent) => {
    if (event.key === "}") {
      event.preventDefault();
      return;
    }
    if (this.target) {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          if (this.activeIndex === this.suggestions.length - 1) {
            this.activeIndex = 0;
          } else {
            this.activeIndex++;
          }
          this.cdr.detectChanges();
          break;
        case "ArrowUp":
          event.preventDefault();
          if (this.activeIndex === 0) {
            this.activeIndex = this.suggestions.length - 1;
          } else {
            this.activeIndex--;
          }
          this.cdr.detectChanges();
          break;
        case "Tab":
        case "Enter":
          event.preventDefault();
          this.removeSearchText();
          insertMention(this.editor, this.suggestions[this.activeIndex]);
          this.cdr.detectChanges();
          break;
        case "Escape":
          event.preventDefault();
          this.target = null;
          this.updateSuggestionsLocation();
          this.cdr.detectChanges();
          break;
      }
    }
  };

  valueChange(value: Element[]) {
    const { selection, operations } = this.editor;
    if (operations[0] == null) {
      return;
    }

    if (
      operations[0].type === "remove_text" &&
      operations[0].text === this.trigger
    ) {
      if (this.target) {
        this.target = null;
        this.updateSuggestionsLocation();
      }

      const afterText = this.editor.string(
        this.editor.range(selection.focus, this.editor.end([])),
      );

      const distance = afterText.indexOf("}") + 1;

      Transforms.delete(this.editor, {
        distance: distance,
        unit: "character",
      });
    }

    if (operations[0].type === "remove_text" && operations[0].text === "}") {
      if (this.target) {
        this.target = null;
        this.updateSuggestionsLocation();
      }

      const beforeText = this.editor.string(
        this.editor.range(selection.focus, this.editor.start([])),
      );

      const distance = beforeText.length - beforeText.indexOf("{");

      Transforms.delete(this.editor, {
        distance: distance,
        unit: "character",
        reverse: true,
      });
    }

    const operationIdx =
      operations[0].type === "remove_text" && operations.length > 1 ? 1 : 0;

    if (
      operations[operationIdx].type === "insert_text" &&
      (operations[operationIdx] as any).text === this.trigger
    ) {
      this.target = {
        anchor: Editor.before(this.editor, selection.anchor),
        focus: selection.focus,
      };
      setTimeout(() => {
        this.editor.insertText("}");
      });
    }
    if (operations[0].type === "insert_text" && operations[0].text === "}") {
      this.editor.move({ reverse: true, distance: 1 });

      this.searchText = "";
      this.activeIndex = 0;
      this.updateSuggestionsLocation();
      return;
    }

    if (selection && Range.isCollapsed(selection) && this.target) {
      const beforeRange = Editor.range(
        this.editor,
        this.target.anchor,
        selection.focus,
      );
      const beforeText = Editor.string(this.editor, beforeRange);
      const beforeMatch = beforeText && beforeText.match(/^\{(\w*(\s\w*)*)$/);
      if (beforeMatch) {
        this.searchText = beforeText.slice(1);
        this.activeIndex = 0;
        this.updateSuggestionsLocation();
        return;
      }
    }
  }

  updateSuggestionsLocation() {
    this.suggestions = this.placeholders
      .filter((suggestion) => {
        return suggestion.toLowerCase().includes(this.searchText.toLowerCase());
      })
      .slice(0, 10);
    if (this.target && this.suggestions.length) {
      const nativeRange = AngularEditor.toDOMRange(this.editor, this.target);
      const rect = nativeRange.getBoundingClientRect();
      this.renderer2.setStyle(
        this.suggestionList.nativeElement,
        "left",
        `${rect.left + window.pageXOffset}px`,
      );
      this.renderer2.setStyle(
        this.suggestionList.nativeElement,
        "top",
        `${rect.top + window.pageYOffset + 25}px`,
      );
      return;
    }
    this.renderer2.removeStyle(this.suggestionList.nativeElement, "left");
    this.renderer2.removeStyle(this.suggestionList.nativeElement, "top");
  }

  mousedown(event: MouseEvent, item) {
    event.preventDefault();
    this.removeSearchText();
    insertMention(this.editor, item);
  }

  removeSearchText() {
    if (this.target) {
      this.target = null;
      this.updateSuggestionsLocation();
    }
    const range = {
      anchor: {
        path: this.editor.selection.anchor.path,
        offset:
          this.editor.selection.anchor.offset - this.searchText.length - 1,
      },
      focus: this.editor.selection.focus,
    };
    Transforms.select(this.editor, range);
    Transforms.delete(this.editor);
  }

  private object2query(object: Element[]) {
    function _recFlatten(line: any) {
      if (line.type == null) {
        return line.text.replaceAll("\n", " ");
      }
      if (line.type === "placeholder") {
        return `{${line.name}}`;
      }
      return line.children.map((l) => _recFlatten(l)).join("");
    }
    const query = object.map((line) => _recFlatten(line)).join("\n");
    return query;
  }

  private query2object(query: string) {
    const lines = query.split("\n");
    const object = lines.map((line) => {
      const children = [];
      const regex = /\{(\w+(\s\w*)*)\}/g;
      const placeholders = [];
      let e;
      do {
        e = regex.exec(line);
        if (e) {
          placeholders.push(e[1]);
        }
      } while (e);
      if (placeholders.length > 0) {
        const splits = line.replaceAll(regex, "|").split("|");
        splits.forEach((l, i) => {
          children.push({ text: l });
          if (i < splits.length - 1) {
            children.push({
              type: "placeholder",
              name: placeholders[i],
              children: [{ text: "" }],
            });
          }
        });
      } else {
        children.push({ text: line });
      }
      return { type: "paragraph", children: children };
    });

    return object;
  }

  getQuery() {
    return this.object2query(this.value);
  }
}
